import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js/min';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country_code: '+91',
    phone: '',
    phone_ext: '',
    company: '',
    country: '',
    website: '',
    message: '',
    service: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const [isCountryCodeOpen, setIsCountryCodeOpen] = useState(false);
  const [countryCodeQuery, setCountryCodeQuery] = useState('');
  const countryCodeRef = useRef(null);

  const COUNTRY_CODES = useMemo(() => {
    let displayNames = null;
    try {
      displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch (e) {
      displayNames = null;
    }

    const entries = getCountries().map((code) => {
      let name = code;
      try {
        name = displayNames ? (displayNames.of(code) || code) : code;
      } catch (e) {
        name = code;
      }
      let dial = '';
      try {
        dial = `+${getCountryCallingCode(code)}`;
      } catch (e) {
        dial = '';
      }
      return { code, name, dial };
    }).filter(c => c.dial);

    entries.sort((a, b) => a.name.localeCompare(b.name));
    return entries;
  }, []);

  const flagForCountryCode = (cc) => {
    try {
      return cc
        .toUpperCase()
        .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));
    } catch (e) {
      return '';
    }
  };

  const selectedCountry = COUNTRY_CODES.find(c => c.dial === formData.country_code) || COUNTRY_CODES.find(c => c.code === 'IN') || COUNTRY_CODES[0];
  const filteredCountryCodes = COUNTRY_CODES.filter(c => {
    const q = countryCodeQuery.trim().toLowerCase();
    if (!q) return true;
    return c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q);
  });

  useEffect(() => {
    if (!isCountryCodeOpen) return;

    const onPointerDown = (e) => {
      const el = countryCodeRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      setIsCountryCodeOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsCountryCodeOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isCountryCodeOpen]);

  const formatLocalPhone = (digits, dialCode) => {
    const d = String(digits || '').replace(/\D/g, '').slice(0, 10);
    if (!d) return '';
    if (dialCode === '+91') {
      if (d.length <= 5) return d;
      return `${d.slice(0, 5)} ${d.slice(5)}`;
    }
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
    return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
  };

  useEffect(() => {
    if (location.state?.jobTitle) {
      setFormData(prev => ({
        ...prev,
        service: 'Job Application',
        message: `I am applying for the position of ${location.state.jobTitle} (ID: ${location.state.jobId}).\n\nLink to portfolio/CV: `
      }));
    }
  }, [location.state]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const serviceParam = params.get('service');
      if (serviceParam) {
        setFormData(prev => ({
          ...prev,
          service: prev.service || serviceParam
        }));
      }
    } catch (e) {
      // ignore
    }
  }, [location.search]);

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Our Office',
      details: [
        'Office No. 1005, Wing A,',
        'Lokmat Bhavan, Ramdaspeth,',
        'Nagpur, Maharashtra 440010, India'
      ]
    },
    {
      icon: Phone,
      title: 'Phone Numbers',
      details: [
        '+91-89832 30676 (India)',
        '+1915-348-7159 (US)'
      ]
    },
    {
      icon: Mail,
      title: 'Email Address',
      details: [
        'Info@Tripodtechnologies.com'
      ]
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: [
        'Monday - Friday: 12:00 PM - 10:00 PM IST',
        '24/7 Support Available'
      ]
    }
  ];

  const services = [
    'IT Outsourcing',
    'IT Consulting', 
    'Software Development',
    'HealthTech Solutions',
    'Algo Trading',
    'Cloud Engineering',
    'Job Application',
    'Other'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phoneDigits = String(formData.phone || '').replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      alert('Phone number must be exactly 10 digits');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, {
        ...formData,
        phone: phoneDigits,
      });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', country_code: formData.country_code || '+91', phone: '', phone_ext: '', company: '', country: '', website: '', message: '', service: '' });
      
      // Show success message for 3 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      
      // Show error message for 3 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 via-purple-50/30 to-cyan-50/30 relative overflow-hidden">
      {/* Tech Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-48 h-48 border border-cyan-300 rounded-lg rotate-12 animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-32 h-32 border border-purple-300 rounded-full"></div>
        <div className="absolute top-1/2 left-10 w-24 h-24 border border-cyan-200 rounded-lg rotate-45"></div>
        {/* Circuit pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(139, 69, 193, 0.05) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      <div className="max-w-8xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            Get In <span className="bg-gradient-to-r from-purple-600 via-cyan-500 to-violet-500 bg-clip-text text-transparent animate-pulse">Touch</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Ready to start your next project? Contact our team of experts for a consultation and let's bring your vision to life.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <Card key={index} className="shadow-lg border-0 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 bg-white/90 backdrop-blur-sm border border-cyan-200/50">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-cyan-50 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-500/20 border border-cyan-200/50">
                        <IconComponent className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text mb-2">{info.title}</h3>
                        <div className="space-y-1">
                          {info.details.map((detail, detailIndex) => (
                            <p key={detailIndex} className="text-gray-600 text-sm">
                              {detail}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-xl border-0 bg-white/95 backdrop-blur-sm border border-cyan-200/50">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">
                  Send Us a Message
                </CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all duration-200 bg-white/90 backdrop-blur-sm hover:shadow-md hover:shadow-cyan-500/10"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all duration-200 bg-white/90 backdrop-blur-sm hover:shadow-md hover:shadow-cyan-500/10"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <div className="grid grid-cols-[92px_minmax(0,1fr)_64px] gap-2">
                        <div ref={countryCodeRef} className="relative">
                          <button
                            type="button"
                            onClick={() => setIsCountryCodeOpen(v => !v)}
                            className="h-full px-2 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 w-full text-left text-sm"
                            aria-label="Select country calling code"
                          >
                            <span className="mr-2">{flagForCountryCode(selectedCountry.code)}</span>
                            <span className="font-medium">{formData.country_code}</span>
                          </button>
                          {isCountryCodeOpen && (
                            <div className="absolute z-20 mt-2 w-[280px] bg-white border border-gray-200 rounded-lg shadow-lg p-2">
                              <input
                                type="text"
                                value={countryCodeQuery}
                                onChange={(e) => setCountryCodeQuery(e.target.value)}
                                placeholder="Search country or code"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
                                autoFocus
                              />
                              <div className="max-h-56 overflow-y-auto">
                                {filteredCountryCodes.map(c => (
                                  <button
                                    key={`${c.code}-${c.dial}`}
                                    type="button"
                                    onClick={() => {
                                      setFormData(prev => ({ ...prev, country_code: c.dial }));
                                      setIsCountryCodeOpen(false);
                                      setCountryCodeQuery('');
                                    }}
                                    className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50"
                                  >
                                    <span className="mr-2">{flagForCountryCode(c.code)}</span>
                                    <span className="font-medium">{c.dial}</span>
                                    <span className="ml-2 text-gray-700">{c.name}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formatLocalPhone(formData.phone, formData.country_code)}
                          onChange={(e) => {
                            const digitsOnly = String(e.target.value || '').replace(/\D/g, '').slice(0, 10);
                            setFormData(prev => ({ ...prev, phone: digitsOnly }));
                          }}
                          required
                          inputMode="numeric"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all duration-200 bg-white/90 backdrop-blur-sm hover:shadow-md hover:shadow-cyan-500/10"
                          placeholder={formData.country_code === '+91' ? '12345 67890' : '123 456 7890'}
                        />
                        <input
                          type="text"
                          id="phone_ext"
                          name="phone_ext"
                          value={formData.phone_ext}
                          onChange={(e) => setFormData(prev => ({ ...prev, phone_ext: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                          inputMode="numeric"
                          className="w-full px-2 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all duration-200 bg-white/90 backdrop-blur-sm hover:shadow-md hover:shadow-cyan-500/10"
                          placeholder="Ext"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all duration-200 bg-white/90 backdrop-blur-sm hover:shadow-md hover:shadow-cyan-500/10"
                        placeholder="Your company name"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                        Country *
                      </label>
                      <input
                        type="text"
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all duration-200 bg-white/90 backdrop-blur-sm hover:shadow-md hover:shadow-cyan-500/10"
                        placeholder="Your country"
                      />
                    </div>
                    <div>
                      <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                        Website
                      </label>
                      <input
                        type="url"
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all duration-200 bg-white/90 backdrop-blur-sm hover:shadow-md hover:shadow-cyan-500/10"
                        placeholder="https://your-website.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      Service Interested In
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all duration-200 bg-white/90 backdrop-blur-sm hover:shadow-md hover:shadow-cyan-500/10"
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all duration-200 resize-none bg-white/90 backdrop-blur-sm hover:shadow-md hover:shadow-cyan-500/10"
                      placeholder="Tell us about your project requirements..."
                    ></textarea>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white py-3 px-6 text-lg font-medium shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-[1.02] border border-cyan-300/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 text-center">
                        ✅ Thank you for your message! We will get back to you within 24 hours.
                      </p>
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 text-center">
                        ❌ There was an error sending your message. Please try again.
                      </p>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
